import React, { useEffect, useState, useCallback } from "react";
import AssociateAppointmentsTable from "./AssociateAppointmentsTable";
import { Button } from "@mui/material";
import ResponseModal from "../../global/ResponseModal";
import LoadingIndicator from "../../global/LoadingIndicator";
import PropTypes from "prop-types";
import { useLoaderData, useRevalidator } from "react-router-dom";

function AssociateAppointments({
  document,
  setOpenAssociateAppointmentsModal,
  closeUploadModal,
  isLaunchedFromAppointmentsTab,
  selectedAppointment,
  appointments,
  nextPageUrl,
  handleOpenAssociateAppointmentModal,
}) {
  const { patchDocument } = useLoaderData();
  const { revalidate } = useRevalidator();
  const [appointmentList, setAppointmentList] = useState([]);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const buttonStyle = {
    width: "120px",
    height: "40px",
  };
  //const provider = document && document.provider ? document.provider : "";
  // TODO: Permissions using the new framework
  /*  const canAddAssociatedDocuments = userRoles.permissions.includes(
    "filemanager.add_document_association"
  ); */
  const canAddAssociatedDocuments = true;
  const handleSaveSuccess = () => {
    if (isLaunchedFromAppointmentsTab) {
      closeUploadModal();
    } else {
      setOpenAssociateAppointmentsModal(false);
    }
  };

  const handleCancel = () => {
    if (isLaunchedFromAppointmentsTab) {
      closeUploadModal();
    } else {
      setOpenAssociateAppointmentsModal(false);
    }
  };

  const handleSaveFail = () => {
    setOpenAssociateAppointmentsModal(false);
  };

  const covertDateFollowFormat = (dateObject) => {
    const dateObj = new Date(dateObject);
    const date = `${(dateObj.getMonth() + 1)
      .toString()
      .padStart(2, "0")}/${dateObj
      .getDate()
      .toString()
      .padStart(2, "0")}/${dateObj.getFullYear()}`;
    const hours = dateObj.getHours();
    const minutes = dateObj.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    const time = `${hours % 12 || 12}:${minutes} ${ampm}`;
    return { date, time };
  };

  const formatAppointments = useCallback((list) => {
    return list.map((app) => {
      const totalAmount = app.administeredServices.reduce((sum, service) => {
        return sum + parseFloat(service.price);
      }, 0);
      const amount = `$${totalAmount.toFixed(2)}`;
      const { date, time } = covertDateFollowFormat(app.scheduled);
      const includedAppointments = isLaunchedFromAppointmentsTab
        ? document?.appointments
        : document?.appointments || [];
      const isIncluded = Array.isArray(includedAppointments)
        ? includedAppointments.includes(app.id)
        : includedAppointments?.id === app.id;

      return {
        id: app.pk,
        date,
        time,
        amount,
        included: isIncluded,
      };
    });
  }, []);

  const handleSaveData = async () => {
    const idsAssociatedAppointments = appointmentList
      .filter((item) => item.included)
      .map((item) => item.id);
    const documentId = document?.pk ? document?.pk : document?.id;
    try {
      let successRes = await patchDocument(documentId, {
        appointments: idsAssociatedAppointments,
      });
      if (successRes) {
        setSaveSuccess(true);
        revalidate();
      }
    } catch (error) {
      setSaveFailed(true);
    }
  };

  const handleScroll = () => {
    if (nextPageUrl && !isLoading) {
      handleOpenAssociateAppointmentModal(document, true, nextPageUrl);
    }
  };

  useEffect(() => {
    let active = true;
    if (active) {
      const allAppointments = formatAppointments(appointments);
      let selAppointment;
      if (selectedAppointment) {
        selAppointment = allAppointments.filter(
          (appt) => appt.id === selectedAppointment.pk
        );
      }

      const responseAppointments = isLaunchedFromAppointmentsTab
        ? selAppointment
        : allAppointments;

      setAppointmentList((prevAppointments) => [
        ...prevAppointments,
        ...responseAppointments,
      ]);

      setIsLoading(false);
    }
    return () => {
      active = false;
    };
  }, [
    appointments,
    formatAppointments,
    isLaunchedFromAppointmentsTab,
    selectedAppointment,
  ]);

  return (
    <>
      {isLoading ? (
        <div className="min-h-[65vh] w-full">
          <LoadingIndicator isLoading={isLoading} />
        </div>
      ) : (
        <>
          <div className="flex w-[100%]">
            {!isLaunchedFromAppointmentsTab && (
              <div className="w-[60%]">
                <div>
                  {document && document.file ? (
                    <object
                      data={document.file}
                      aria-label={document.file_name}
                      style={{
                        width: "100%",
                        height: "auto",
                        display: "block",
                        minHeight: "60vh",
                      }}
                    />
                  ) : (
                    <p
                      style={{
                        fontSize: "1.2rem",
                        textAlign: "center",
                        fontWeight: "bold",
                      }}
                    >
                      No File Preview Available
                    </p>
                  )}
                </div>
              </div>
            )}
            <div className="grow ">
              {appointmentList.length > 0 && (
                <AssociateAppointmentsTable
                  next={nextPageUrl}
                  document={document}
                  includedAppointments={
                    isLaunchedFromAppointmentsTab
                      ? document
                      : document.appointments
                  }
                  appointmentList={appointmentList}
                  setAppointmentList={setAppointmentList}
                  handleScroll={handleScroll}
                  canAddAssociatedDocuments={canAddAssociatedDocuments}
                />
              )}
            </div>
          </div>
          <div>
            {canAddAssociatedDocuments && (
              <div className="flex mt-6 justify-end space-between gap-4">
                <Button
                  style={buttonStyle}
                  variant="contained"
                  color="error"
                  onClick={() => handleCancel()}
                >
                  Cancel
                </Button>
                <Button
                  disabled={!appointmentList || appointmentList.length === 0}
                  style={buttonStyle}
                  variant="contained"
                  color="primary"
                  onClick={() => handleSaveData()}
                >
                  Save
                </Button>
              </div>
            )}
          </div>
          <ResponseModal
            title="Appointments successfully associated and saved"
            description="You've updated the Association of Appointments to Documents successfully"
            openBool={saveSuccess}
            setOpenBool={setSaveSuccess}
            handleCloseFunc={handleSaveSuccess}
          />
          <ResponseModal
            title="Can't save your associated appointments"
            isError={true}
            openBool={saveFailed}
            setOpenBool={setSaveFailed}
            errorMessage={null}
            handleCloseFunc={handleSaveFail}
          />
        </>
      )}
    </>
  );
}

AssociateAppointments.propTypes = {
  document: PropTypes.shape({
    pk: PropTypes.number,
    id: PropTypes.number,
    appointments: PropTypes.array,
    file: PropTypes.string,
    file_name: PropTypes.string,
    provider: PropTypes.string || PropTypes.number,
  }),
  setOpenAssociateAppointmentsModal: PropTypes.func,
  closeUploadModal: PropTypes.bool,
  isLaunchedFromAppointmentsTab: PropTypes.bool,
  selectedAppointment: PropTypes.array,
  appointments: PropTypes.array,
};

export default AssociateAppointments;
